/**
 * Feature Flags Nav Item
 *
 * A navigation item for opening the Feature Flags panel.
 * Only rendered in development mode.
 */

import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import Button from 'components/common/Button';
import { FocusGroup, FocusTrap } from 'components/focus';
import Selectable from 'components/focus/Selectable';
import focusManager from 'support/focus/FocusManager';
import appActions from 'common/support/applicationActions';
import { useFeatureFlags } from 'components/providers/FeatureFlagProvider';
import {
  loadOverrides,
  saveOverrides,
  type FeatureFlagOverrides,
} from 'components/providers/featureFlagOverrides';
import styles from './LeftNav.module.scss';
import panel from './FeatureFlagsNavItem.module.scss';

interface FeatureFlagsNavItemProps {
  index: number;
  isFocused?: boolean;
  isExpanded?: boolean;
}

const FeatureFlagsNavItem: React.FC<FeatureFlagsNavItemProps> = ({
  index,
  isFocused = false,
  isExpanded = false,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>(() => loadOverrides());
  const { features, user } = useFeatureFlags();

  const onClick = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const handleOverridesChange = useCallback((newOverrides: FeatureFlagOverrides) => {
    // Just update local state - don't save or reload yet
    setOverrides(newOverrides);
  }, []);

  const handleSaveAndReload = useCallback(() => {
    saveOverrides(overrides);
    // Reload page to apply new overrides
    window.location.reload();
  }, [overrides]);

  const handleClose = useCallback(() => {
    // Reset to saved overrides when closing without saving
    setOverrides(loadOverrides());
    setIsPanelOpen(false);
  }, []);

  // Count active overrides for badge
  const overrideCount = Object.keys(overrides).length;

  return (
    <>
      <Button
        className={classNames(isFocused && styles['has-focus'], styles.NavItem)}
        size="md"
        focusId={`left-nav-${index}`}
        onClick={onClick}
        ttsText="Feature Flags"
      >
        {/* Flag emoji as icon - matches staticImage class dimensions */}
        <span className={classNames(styles.staticImage, styles.featureFlagsIcon)}>
          🚩
        </span>
        <span
          className={classNames(
            styles.text,
            styles.featureFlagsText,
            isExpanded ? '' : styles['minimized-text'],
          )}
        >
          Feature Flags
          {overrideCount > 0 && (
            <span className={styles.featureFlagsBadge}>{overrideCount}</span>
          )}
        </span>
      </Button>

      {/* Feature Flag Panel */}
      {isPanelOpen && (
        <FeatureFlagPanel
          features={features}
          user={user as Record<string, unknown>}
          overrides={overrides}
          savedOverrides={loadOverrides()}
          onOverridesChange={handleOverridesChange}
          onSaveAndReload={handleSaveAndReload}
          onClose={handleClose}
        />
      )}
    </>
  );
};

// =============================================================================
// Feature Flag Panel (Modal/Overlay version for TV navigation)
// =============================================================================

interface FeatureFlagPanelProps {
  features: Record<string, boolean | undefined>;
  user: Record<string, unknown>;
  overrides: FeatureFlagOverrides;
  savedOverrides: FeatureFlagOverrides;
  onOverridesChange: (overrides: FeatureFlagOverrides) => void;
  onSaveAndReload: () => void;
  onClose: () => void;
}

interface FlagRowProps {
  flagId: string;
  isOn: boolean;
  hasOverride: boolean;
  onToggle: (flagId: string) => void;
}

const FlagRow: React.FC<FlagRowProps> = ({ flagId, isOn, hasOverride, onToggle }) => {
  const rowRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(() => {
    onToggle(flagId);
  }, [onToggle, flagId]);

  // Keep the focused row visible inside the scrollable content area.
  const handleFocusSet = useCallback((hasFocus: boolean) => {
    if (hasFocus && rowRef.current) {
      try {
        rowRef.current.scrollIntoView({ block: 'nearest' });
      } catch {
        rowRef.current.scrollIntoView(false);
      }
    }
  }, []);

  return (
    <div ref={rowRef} className={panel.flagRowWrapper}>
      <Selectable
        onSelect={handleSelect}
        onFocusSet={handleFocusSet}
        ttsText={`${flagId}, ${isOn ? 'enabled' : 'disabled'}`}
        render={({ hasFocus }) => (
          <div
            className={classNames(panel.flagRow, hasFocus && panel.flagRowFocused)}
            onClick={handleSelect}
          >
            <div className={panel.flagInfo}>
              <div className={panel.flagName}>
                {flagId}
                {hasOverride && <span className={panel.overridden}>(overridden)</span>}
              </div>
              <div className={classNames(panel.flagStatus, isOn ? panel.enabled : panel.disabled)}>
                {isOn ? '\u25cf Enabled' : '\u25cb Disabled'}
              </div>
            </div>

            <span className={classNames(panel.toggleBtn, isOn ? panel.toggleOn : panel.toggleOff)}>
              {isOn ? 'ON' : 'OFF'}
            </span>
          </div>
        )}
      />
    </div>
  );
};

interface PanelButtonProps {
  focusId: string;
  onSelect: () => void;
  ttsText: string;
  className?: string;
  children: ReactNode;
}

// Compact, panel-styled focusable button. The shared Button component
// carries full-screen TV dimensions (293x95, 36px font) that don't fit
// this dev panel, so we build on Selectable directly.
const PanelButton: React.FC<PanelButtonProps> = ({
  focusId,
  onSelect,
  ttsText,
  className,
  children,
}) => (
  <Selectable
    focusId={focusId}
    onSelect={onSelect}
    ttsText={ttsText}
    render={({ hasFocus }) => (
      <button
        type="button"
        className={classNames(panel.btn, className, hasFocus && panel.btnFocused)}
        onClick={onSelect}
      >
        {children}
      </button>
    )}
  />
);

const PANEL_FOCUS_ID = 'feature-flag-panel';

const FeatureFlagPanel: React.FC<FeatureFlagPanelProps> = ({
  features,
  overrides,
  savedOverrides,
  onOverridesChange,
  onSaveAndReload,
  onClose,
}) => {
  const flagIds = Object.keys(features);

  // Check if there are unsaved changes
  const hasUnsavedChanges = JSON.stringify(overrides) !== JSON.stringify(savedOverrides);

  const handleToggle = (flagId: string) => {
    const currentValue = overrides[flagId] ?? features[flagId];
    const newOverrides = {
      ...overrides,
      [flagId]: !currentValue,
    };
    onOverridesChange(newOverrides);
  };

  const handleReset = () => {
    onOverridesChange({});
  };

  const getEffectiveValue = (flagId: string): boolean => {
    if (flagId in overrides) {
      return overrides[flagId];
    }
    return features[flagId] ?? false;
  };

  const isOverridden = (flagId: string): boolean => {
    return flagId in overrides;
  };

  const hasOverrides = Object.keys(overrides).length > 0;

  const handleResetGuarded = useCallback(() => {
    if (hasOverrides) {
      handleReset();
    }
  }, [hasOverrides]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveGuarded = useCallback(() => {
    if (hasUnsavedChanges) {
      onSaveAndReload();
    }
  }, [hasUnsavedChanges, onSaveAndReload]);

  // Move focus into the panel on open, restore it on close.
  useEffect(() => {
    const previousFocusId = focusManager.currentFocusId;
    focusManager.setCurrentFocus(PANEL_FOCUS_ID);
    return () => {
      if (previousFocusId) {
        focusManager.setCurrentFocus(previousFocusId);
      }
    };
  }, []);

  // The remote's back action (Esc on desktop, 461 on LG, KEY_RETURN on
  // Samsung) closes the panel. FocusTrap keeps all other actions from
  // reaching the app behind the overlay.
  const onActionOverride = (action: string): boolean => {
    if (action === appActions.back) {
      onClose();
      return true;
    }
    return false;
  };

  return (
    <div className={panel.overlay} onClick={onClose}>
      <div className={panel.panel} onClick={e => e.stopPropagation()}>
        <FocusTrap>
          {/* Header */}
          <div className={panel.header}>
            <h2 className={panel.title}>🚩 Feature Flags</h2>
            <span className={panel.headerHint}>Press BACK to close</span>
          </div>

          <FocusGroup
            focusId={PANEL_FOCUS_ID}
            onActionOverride={onActionOverride}
            wrapStart
            wrapEnd
          >
            {/* Content */}
            <div className={panel.content}>
              {flagIds.length === 0 ? (
                <div className={panel.emptyState}>No feature flags defined</div>
              ) : (
                flagIds.map(flagId => (
                  <FlagRow
                    key={flagId}
                    flagId={flagId}
                    isOn={getEffectiveValue(flagId)}
                    hasOverride={isOverridden(flagId)}
                    onToggle={handleToggle}
                  />
                ))
              )}

              <div className={panel.hint}>
                💡 Toggle flags, then select "Save & Reload" to apply.
                {hasUnsavedChanges && (
                  <div className={panel.hintWarning}>
                    ⚠ You have unsaved changes
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <FocusGroup horizontal className={panel.footer}>
              <PanelButton
                focusId="ff-panel-reset"
                onSelect={handleResetGuarded}
                ttsText="Reset All"
                className={classNames(panel.resetBtn, !hasOverrides && panel.btnDisabled)}
              >
                Reset All
              </PanelButton>
              <PanelButton
                focusId="ff-panel-save"
                onSelect={handleSaveGuarded}
                ttsText="Save and Reload"
                className={hasUnsavedChanges ? panel.saveBtnActive : panel.saveBtnInactive}
              >
                Save & Reload
              </PanelButton>
              <PanelButton
                focusId="ff-panel-cancel"
                onSelect={onClose}
                ttsText="Cancel"
                className={panel.closeFooterBtn}
              >
                Cancel
              </PanelButton>
            </FocusGroup>
          </FocusGroup>
        </FocusTrap>
      </div>
    </div>
  );
};

export default FeatureFlagsNavItem;
